/** @jsxImportSource @emotion/react */
import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import _ from 'lodash';
import { AutoSizeMode, DataMap } from '@grapecity/wijmo.grid';
import { Button } from '@mui/material';
import { ComboBox } from 'components/selects/ComboBox';
import CustomChip from 'components/selects/CustomChip';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import CustomGrid from 'components/grids/CustomGrid';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import {
  findEquipmentSpec,
  findSpecManagerMyItems,
  saveEquipmentSpec,
} from 'apis/md/MdEquipmentSpec';
import { uploadExcelUtMatrixTemplates } from 'apis/common/Excel';
import { CommonUtil } from 'utils/CommonUtil';
import ExcelUploadButton from 'components/buttons/ExcelUploadButton';
import ExcelValidModal from 'components/modals/common/ExcelValidModal';
import { downloadExcelTemplatesPost } from 'apis/common/Excel';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'hooks/useMessageBar';
import { CrudCode } from 'models/common/Edit';
import { MdSpecManager } from 'models/md/MdEquipmentSpec';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import EltrCellLineInqPopup from '../popup/EltrCellLineInqPopup';
import { MdLine } from 'models/md/MdLine';
import { useCommonModal } from 'hooks/useCommonModal';
import { FileTypeName } from 'models/common/Common';
import { Code } from 'models/common/CommonCode';
import { HeaderGroupMergeManager } from './MdEqpSpecInqPage';

/**
 * @desc 투자비관리 > 기준정보 > 설비사양 등록
 *    -> 설비사양 > 설비사양관리 > 설비사양 등록
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.05.26   jshwang12       신규 생성
 * 2026.08.28   jshwang12       설비군 다건 등록/수정 (UI, 엑셀)
 */
const MdEqpSpecRegistPage = () => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const [myItems, setMyItems] = useState<MdSpecManager[]>();
  const [rowData, setRowData] = useState<any[]>([]);
  const [code, setCode] = useState<any>();
  const [condition, setCondition] = useState<any>();
  const [isOpenAddLinePopup, setOpenAddLinePopup] = useState<boolean>(false);
  const [excelValidCondition, setExcelValidCondition] = useState<string | null>(null);
  const [isExcelValidModalOpen, setExcelValidModalOpen] = useState<boolean>(false);
  const [hasAuth, setHasAuth] = useState<boolean>(false);

  useEffect(() => {
    getCommonCodes();
  }, []);

  useEffect(() => {
    handleSearch(condition);
  }, [condition]);

  /**
   * 검색조건 > 플랜트 (사양 담당자로 등록된 항목만 노출)
   */
  const optionsBildPlntCd = useMemo(() => {
    if ((myItems || []).length < 1) return [];
    return (code?.bildPlntCd || []).filter((o) =>
      (myItems || []).find((my) => my.bildPlntCd === o.cmnCd)
    );
  }, [code, myItems]);

  useEffect(() => {
    // 설비사양 담당자로 등록된 경우 저장 가능 (일반사용자도 설비사양담당자로 등록된 경우 저장 가능)
    if ((optionsBildPlntCd || []).length > 0) {
      setHasAuth(true);
    }
  }, [optionsBildPlntCd]);

  /**
   * 검색조건 > 라인유형 (사양 담당자로 등록된 항목만 노출)
   */
  const optionsPrdnLnTpCd = useMemo(() => {
    if ((myItems || []).length < 1 || !condition?.bildPlntCd) return [];
    const targets = (myItems || [])
      .filter((my) => my.bildPlntCd === condition?.bildPlntCd)
      .map((o) => o.prdnLnTpCd || '');
    return (code?.mdPrdnLnTpCd || []).filter((o) => (targets || []).includes(o.cmnCd));
  }, [code, myItems, condition]);

  /**
   * 검색조건 > 설비군 (사양 담당자로 등록된 항목만 노출)
   */
  const optionsEqclId = useMemo(() => {
    if ((myItems || []).length < 1 || !condition?.bildPlntCd || !condition?.prdnLnTpCd) return [];
    const targets = (myItems || [])
      .filter(
        (my) => my.bildPlntCd === condition?.bildPlntCd && my.prdnLnTpCd === condition?.prdnLnTpCd
      )
      .map((o) => o.eqclId || '');
    return (code?.mdEqclId || []).filter((o) => (targets || []).includes(o.cmnCd));
  }, [code, myItems, condition]);

  /**
   * 검색조건 > 설비군 선택 옵션 (전체 포함)
   */
  const chipOptionsEqclId = useMemo(() => {
    if ((optionsEqclId || []).length < 1) return [];
    return [
      { cmnCd: 'ALL', cmnCdNm: String(t('com.label.전체', '전체')) } as Code,
      ...optionsEqclId,
    ];
  }, [optionsEqclId]);

  /**
   * 선택된 설비군 목록 (전체 선택 시 전체 설비군으로 확장)
   */
  const getTargetEqclIds = (eqclIds?: string[]) => {
    const selected = eqclIds || [];
    if (selected.includes('ALL')) {
      return (optionsEqclId || []).map((o) => o.cmnCd || '');
    }
    return selected;
  };

  const targetEqclIds = useMemo(() => {
    return getTargetEqclIds(condition?.eqclIds);
  }, [condition, optionsEqclId]);

  const getCommonCodes = () => {
    Promise.all([
      getCommonCodeNames('BILD_PLNT_CD'),
      getCommonCodeNames('MD_PRDN_LN_TP_CD'),
      getCommonCodeNamesCondition({
        cmnGrCd: 'MD_EQCL_ID',
        optValCtn1: 'Y', // 설비사양대상 (설비기준)
      }),
      getCommonCodeNamesCondition({
        cmnGrCd: 'MD_EQCL_SPEC_MAPPING',
        optValCtn9: 'Y', // 템플릿업로드(화면표시) 항목여부
      }),
      findSpecManagerMyItems(),
    ]).then(async ([bildPlntCd, mdPrdnLnTpCd, mdEqclId, mdEqclSpecMapping, specManagerMyItems]) => {
      setMyItems(specManagerMyItems);
      const colGrCds = (mdEqclSpecMapping || []).reduce((acc, cur) => {
        // 데이터 검증(NUM or 그룹코드)
        if (cur.optValCtn13 && cur.optValCtn13 !== 'NUM' && !acc.includes(cur.optValCtn13)) {
          acc.push(cur.optValCtn13);
        }
        return acc;
      }, [] as string[]);
      const colGrCdsResults = await Promise.all(
        colGrCds.map((cmnGrCd) => getCommonCodeNames(cmnGrCd))
      );
      const mapColGrCds = colGrCds.reduce((acc, cmnGrCd, index) => {
        acc[cmnGrCd] = colGrCdsResults[index];
        return acc;
      }, {} as Record<string, Code[]>);
      setCode({
        bildPlntCd: bildPlntCd,
        mdPrdnLnTpCd: mdPrdnLnTpCd,
        mdEqclId: mdEqclId,
        mdEqclSpecMapping: mdEqclSpecMapping,
        colGrCds: mapColGrCds, // 그리드 셀 콤보박스에서 사용되는 공통코드 (ex. {MD_CELL_TP: [] as Code[], ...})
      });
    });
  };

  const handleSearch = (params) => {
    // 전체(ALL) 선택 시 실제 설비군 목록으로 확장하여 조회
    const eqclIds = getTargetEqclIds(params?.eqclIds);
    if (params?.bildPlntCd && params?.prdnLnTpCd && eqclIds.length > 0) {
      findEquipmentSpec({
        ...params,
        eqclIds: eqclIds,
        bildPlntCds: [params?.bildPlntCd],
      }).then((result) => {
        if (Object.keys(result || {}).find((k) => k === params?.prdnLnTpCd)) {
          setRowData(result[params?.prdnLnTpCd]);
        } else {
          setRowData([]);
        }
      });
    } else {
      setRowData([]);
    }
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const handleDownloadExcel = () => {
    if (!condition?.bildPlntCd || !condition?.prdnLnTpCd || (targetEqclIds || []).length < 1) {
      openMessageBar({
        type: 'warning',
        content: String(
          t(
            'md.label.플랜트/라인유형/설비군을 선택해 주세요.',
            '플랜트/라인유형/설비군을 선택해 주세요.'
          )
        ),
      });
      return;
    }

    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('md.label.설비사양', '설비사양'),
      t('md.label.설비사양', '설비사양')
    );
  };

  /**
   * 행추가
   */
  const handleAddRow = useEvent((newLines: MdLine[]) => {
    // crudKey가 CREATE가 아닌 항목들만 유지 (이미 저장된 항목)
    const existingRows = rowData.filter((row) => row.crudKey !== CrudCode.CREATE);

    const columns = (code?.mdEqclSpecMapping || []).filter(
      (o) =>
        condition?.prdnLnTpCd &&
        o.optValCtn5 === condition?.prdnLnTpCd &&
        (targetEqclIds || []).includes(o.optValCtn1)
    );

    const newRows = (newLines || [])
      .filter((o) => !(existingRows || []).some((row) => row['PRDN_LN_ID'] === o.prdnLnId))
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            crudKey: CrudCode.CREATE,
            BILD_PLNT_CD: cur.bildPlntCd,
            BILD_PLNT_CD_NM: cur.bildPlntCdNm,
            BLDO_ID: cur.bldoId,
            BLDO_NM: cur.bldoNm,
            PRDN_LN_ID: cur.prdnLnId,
            PRDN_LN_NM: cur.prdnLnNm,
            PRDN_LN_TP_CD: cur.prdnLnTpCd,
            PRDN_LN_TP_CD_NM: cur.prdnLnTpCdNm,
            ...(columns || []).reduce(
              (acc, cur) => Object.assign(acc, { [`'${cur.cmnCd}'`]: '' }),
              {} as any
            ),
          },
        ],
        [] as any[]
      );

    setRowData([...newRows, ...existingRows]);
  });

  /**
   * 행상제
   */
  const handleDelRow = () => {
    const selectedRows = gridRef.current?.rows?.filter((r) => r.isSelected);
    if (!selectedRows) return;

    const selectedIds = selectedRows
      .map((row) => {
        return parseInt(row.index!);
      })
      .reverse();

    selectedIds.forEach((item) => {
      if (rowData[item].crudKey === CrudCode.CREATE) {
        delete rowData[item];
      } else {
        rowData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = rowData.filter((element) => element !== undefined);
    setRowData(filteredData);
  };

  const downloadExcelTemplate = () => {
    if (condition?.bildPlntCd && condition?.prdnLnTpCd && (targetEqclIds || []).length > 0) {
      openLoading(true);
      downloadExcelTemplatesPost(
        FileTypeName.TPL_MD_EQP_SPEC,
        '',
        [],
        condition?.bildPlntCd,
        condition?.prdnLnTpCd,
        // 다건의 설비군은 콤마로 구분하여 전달 (ex. COT,MIX)
        (targetEqclIds || []).join(',')
      ).finally(() => openLoading(false));
    } else {
      openMessageBar({
        type: 'warning',
        content: String(
          t(
            'md.label.플랜트/라인유형/설비군을 선택해 주세요.',
            '플랜트/라인유형/설비군을 선택해 주세요.'
          )
        ),
      });
    }
  };

  const validate = () => {
    // 수정사항 없음
    if ((rowData || []).filter((o) => !!o.crudKey).length < 1) {
      return false;
    }
    const valid = (rowData || [])
      .filter((o) => o.crudKey !== CrudCode.DELETE)
      .map((o, index) => {
        if (_.isEmpty(o['PRDN_LN_ID'])) {
          return `${index + 1} : ${t(`md.label.생산라인ID가 없습니다.`, `생산라인ID가 없습니다.`)}`;
        }
      })
      .filter((element) => element !== undefined);

    if (valid.length) {
      openMessageBar({ type: 'error', content: valid.join('<br/>') });
      return false;
    }

    return true;
  };

  /**
   * 저장
   */
  const handleSave = () => {
    if (!validate()) {
      return;
    }
    const confirmMsg = (rowData || []).find((o) => CrudCode.DELETE === o.crudKey)
      ? t(
          'md.label.행삭제 시 선택한 행의 사양정보가 삭제됩니다.<br/>변경사항을 저장하시겠습니까?',
          '행삭제 시 선택한 행의 사양정보가 삭제됩니다.<br/>변경사항을 저장하시겠습니까?'
        )
      : t('com.label.저장하시겠습니까?', '저장하시겠습니까?');

    openCommonModal({
      modalType: 'confirm',
      content: confirmMsg,
      yesCallback: () => {
        saveEquipmentSpec(rowData).then((res) => {
          openMessageBar({
            type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
            content:
              res?.successOrNot === 'Y'
                ? t('com.label.저장되었습니다.', '저장되었습니다.')
                : res.data && typeof res.data === 'string'
                ? res.data
                : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
          });
          if (res?.successOrNot === 'Y') {
            handleSearch(condition);
          }
        });
      },
    });
  };

  const convertPrdnLnTpLayout = (mappingCds) => {
    const groupMid = (mappingCds || []).reduce((acc, cur) => {
      const topCtgrNm = cur.optValCtn6; // 그리드 상단(1번째)
      const midCtgrNm = cur.optValCtn7; // 그리드 중간(2번째)
      if (!acc[topCtgrNm]) {
        acc[topCtgrNm] = {};
      }
      if (!acc[topCtgrNm][midCtgrNm]) {
        acc[topCtgrNm][midCtgrNm] = [];
      }
      acc[topCtgrNm][midCtgrNm].push({
        binding: `'${cur.cmnCd}'`,
        header: cur.optValCtn8, // 그리드 하단(3번째)
        minWidth: 80,
        width: 100,
        align: 'center',
        isRequired: false,
        ...(cur.optValCtn13 &&
          cur.optValCtn13 !== 'NUM' && {
            cssClass: 'WijmoSelect',
            dataMap: new DataMap(code?.colGrCds?.[cur.optValCtn13] || [], 'cmnCd', 'cmnCdNm'),
          }),
      });
      return acc;
    }, {});

    const columns = [] as any[];
    Object.keys(groupMid).forEach((topCtgrNm) => {
      const topCol = {
        header: topCtgrNm,
        columns: [] as any[],
      };

      Object.keys(groupMid[topCtgrNm]).forEach((midCtgrNm) => {
        topCol.columns.push({
          header: midCtgrNm,
          columns: groupMid[topCtgrNm][midCtgrNm],
        });
      });
      columns.push(topCol);
    });
    return columns;
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        header: String(t('com.label.상태', '상태')),
        binding: 'crudKey',
        minWidth: 40,
        width: 43,
        isReadOnly: true,
        align: 'center',
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'BILD_PLNT_CD_NM',
        header: String(t('com.label.플랜트', '플랜트')),
        minWidth: 90,
        width: 154,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'BLDO_NM',
        header: String(t('com.label.동 명', '동 명')),
        minWidth: 50,
        width: 90,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'PRDN_LN_NM',
        header: String(t('md.label.생산라인', '생산라인')),
        minWidth: 100,
        width: '*',
        align: 'center',
        isReadOnly: true,
      },
      ...convertPrdnLnTpLayout(
        (code?.mdEqclSpecMapping || []).filter(
          (o) =>
            condition?.prdnLnTpCd &&
            o.optValCtn5 === condition?.prdnLnTpCd &&
            (targetEqclIds || []).includes(o.optValCtn1)
        )
      ),
    ];
  }, [code, condition]);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (gridRef.current?.hostElement && (gridRef.current?.columns || []).length > 4) {
        gridRef.current.autoSizeColumns();
      }
    });
  }, [layoutDefinition]);

  const numCols = useMemo(() => {
    return (code?.mdEqclSpecMapping || [])
      .filter((o) => o.optValCtn4 === 'Y' || o.optValCtn13 === 'NUM') // 설비대수 or 검증(NUM)
      .map((o) => `'${o.cmnCd}'`);
  }, [code?.mdEqclSpecMapping]);

  const onCellEditEnding = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    const newVal = grid.activeEditor?.value;

    // 설비대수 or 검증(NUM)인 경우
    if (numCols.includes(binding)) {
      // 숫자만 입력 가능
      if (newVal) {
        if (!/^[0-9]+$/.test(newVal)) {
          e.cancel = true;
          return;
        }
      }
    }
  });

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.autoSizeMode = AutoSizeMode.Headers;
    // 상위 헤더가 다른 경우 하위 헤더 병합 방지
    grid.mergeManager = new HeaderGroupMergeManager();
    grid.updatedLayout.addHandler(function s() {
      grid.updatedLayout.removeHandler(s);

      if (grid.columns.length > 4) {
        grid.autoSizeColumns();
      }
    });
  };

  // 설비군 칩이 가로로 넘칠 때 아래로 줄바꿈: 기본 레이아웃은 높이 28px 고정
  const eqclChipLayoutCss = {
    height: 'auto',
    alignItems: 'flex-start',
  };
  const eqclChipGroupCss = {
    maxWidth: '100%',
    alignItems: 'flex-start',
    // 라벨은 첫 줄 칩(28px) 높이에 맞춰 세로 정렬
    '& > label': { lineHeight: '28px', whiteSpace: 'nowrap' as const },
    '& .MuiStack-root': {
      flexWrap: 'wrap' as const,
      columnGap: '4px',
      rowGap: '4px',
      height: 'auto',
    },
    // 줄바꿈 시 줄 첫 칩이 밀리지 않도록 margin 대신 gap 사용
    '& .MuiStack-root .MuiChip-root + .MuiChip-root': { marginLeft: 0 },
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.설비사양 목록', '설비사양 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <label>{String(t('com.label.플랜트', '플랜트'))}</label>
          <ComboBox
            placeholder={String(t('com.label.선택', '선택'))}
            options={optionsBildPlntCd}
            defaultValue={condition?.bildPlntCd}
            style={{ width: '150px' }}
            onChange={(value) => {
              setCondition((prev) => ({
                ...prev,
                prdnLnTpCd: '',
                eqclIds: [],
              }));
              handleOnChange('bildPlntCd', value);
            }}
          />
          <label style={{ marginLeft: '12px' }}>{String(t('md.label.라인유형', '라인유형'))}</label>
          <ComboBox
            placeholder={String(t('com.label.선택', '선택'))}
            options={optionsPrdnLnTpCd}
            defaultValue={condition?.prdnLnTpCd}
            style={{ width: '90px' }}
            onChange={(value) => {
              setCondition((prev) => ({
                ...prev,
                eqclIds: [],
              }));
              handleOnChange('prdnLnTpCd', value);
            }}
          />
          {hasAuth && (
            <>
              <ExcelUploadButton
                msgConfirm={
                  t(
                    'md.label.엑셀 양식에 작성된 내용으로 추가/수정됩니다.<br/>새로 Upload 하시겠습니까?',
                    '엑셀 양식에 작성된 내용으로 추가/수정됩니다.<br/>새로 Upload 하시겠습니까?'
                  ) || ''
                }
                onPreCheck={async () => {
                  if (
                    !condition?.bildPlntCd ||
                    !condition?.prdnLnTpCd ||
                    (targetEqclIds || []).length < 1
                  ) {
                    openMessageBar({
                      type: 'warning',
                      content: String(
                        t(
                          'md.label.플랜트/라인유형/설비군을 선택해 주세요.',
                          '플랜트/라인유형/설비군을 선택해 주세요.'
                        )
                      ),
                    });
                    return false;
                  }
                  return true;
                }}
                onCallback={(file) => {
                  openLoading(true);
                  uploadExcelUtMatrixTemplates(
                    file,
                    'TPL_MD_EQP_SPEC',
                    'NORMAL',
                    'TB_EELMB_MD_EQCL_SPEC_M',
                    condition.prdnLnTpCd, // id
                    // 다건의 설비군은 콤마로 구분하여 전달 (ex. COT,MIX)
                    (targetEqclIds || []).join(',') // detailId
                  )
                    .then((response) => {
                      if (response?.data.x_result == 'OK') {
                        openCommonModal({
                          modalType: 'alert',
                          content: t(
                            'md.label.업로드한 설비사양정보가 저장되었습니다.',
                            '업로드한 설비사양정보가 저장되었습니다.'
                          ),
                        });
                        handleSearch(condition);
                      } else {
                        setExcelValidCondition(response?.data.p_xls_upload_id);
                        setExcelValidModalOpen(true);
                      }
                    })
                    .finally(() => {
                      openLoading(false);
                    });
                }}
              />
              <Button
                css={IconButton.button}
                className="Exceldown"
                onClick={() => downloadExcelTemplate()}
                disableRipple
              >
                {t('com.button.템플릿 다운로드', '템플릿 다운로드')}
              </Button>
              <Button
                css={IconButton.button}
                className="plus"
                onClick={() => {
                  if (
                    !condition?.bildPlntCd ||
                    !condition?.prdnLnTpCd ||
                    (targetEqclIds || []).length < 1
                  ) {
                    openMessageBar({
                      type: 'warning',
                      content: String(
                        t(
                          'md.label.플랜트/라인유형/설비군을 선택해 주세요.',
                          '플랜트/라인유형/설비군을 선택해 주세요.'
                        )
                      ),
                    });
                    return;
                  }
                  setOpenAddLinePopup(true);
                }}
                disableRipple
              >
                {t('md.label.라인 추가', '라인 추가')}
              </Button>
              <Button
                css={IconButton.button}
                className="delRow"
                onClick={handleDelRow}
                disableRipple
              >
                {t('com.button.행삭제', '행삭제')}
              </Button>
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(condition)}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      {(chipOptionsEqclId || []).length > 0 && (
        <SubTitleLayout css={eqclChipLayoutCss}>
          <ControlBtnGroup css={eqclChipGroupCss}>
            <label>{String(t('md.label.설비군', '설비군'))}</label>
            <CustomChip
              options={chipOptionsEqclId}
              defaultValue={condition?.eqclIds || []}
              singleSelect={false}
              onChange={(value) => {
                let values = (value || []) as string[];
                if (values.includes('ALL')) {
                  // 전체가 선택된 상태에서 다른 항목을 고르면 전체를 해제
                  if ((condition?.eqclIds || []).includes('ALL') && values.length > 1) {
                    values = values.filter((v) => v !== 'ALL');
                  } else {
                    values = ['ALL'];
                  }
                }
                handleOnChange('eqclIds', values);
              }}
            />
          </ControlBtnGroup>
        </SubTitleLayout>
      )}
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        isReadOnly={!hasAuth}
        height={
          (targetEqclIds || []).length > 0 ? 522 : (chipOptionsEqclId || []).length > 0 ? 596 : 636
        }
        frozenColumns={3}
        initialized={onInitialized}
        cellEditEnding={onCellEditEnding}
      />
      {hasAuth && (
        <GlobalBtnGroup>
          <Button css={IconButton.button} className="save" onClick={handleSave} disableRipple>
            {t('com.label.저장', '저장')}
          </Button>
        </GlobalBtnGroup>
      )}
      {isOpenAddLinePopup && condition?.prdnLnTpCd && condition?.bildPlntCd && (
        <EltrCellLineInqPopup
          open={isOpenAddLinePopup}
          close={() => setOpenAddLinePopup(false)}
          condition={{
            prdnLnTpCd: condition?.prdnLnTpCd,
            prdnLnTpCdNm: (code?.mdPrdnLnTpCd || []).find((o) => o.cmnCd === condition?.prdnLnTpCd)
              ?.cmnCdNm,
            bildPlntCds: [condition?.bildPlntCd],
          }}
          selectedItems={(rowData || [])
            .filter((o) => CrudCode.CREATE === o.crudKey)
            .reduce(
              (acc, cur) => [
                ...acc,
                {
                  bildPlntCd: cur['BILD_PLNT_CD'],
                  bildPlntCdNm: cur['BILD_PLNT_CD_NM'],
                  bldoId: cur['BLDO_ID'],
                  bldoNm: cur['BLDO_NM'],
                  prdnLnId: cur['PRDN_LN_ID'],
                  prdnLnNm: cur['PRDN_LN_NM'],
                  prdnLnTpCd: cur['PRDN_LN_TP_CD'],
                  prdnLnTpCdNm: cur['PRDN_LN_TP_CD_NM'],
                },
              ],
              [] as MdLine[]
            )}
          callbackFn={(result) => {
            handleAddRow(result);
          }}
        />
      )}

      {isExcelValidModalOpen && (
        <ExcelValidModal
          open={isExcelValidModalOpen}
          close={() => {
            setExcelValidCondition(null);
            setExcelValidModalOpen(false);
          }}
          condition={{
            valReqId: excelValidCondition || '',
          }}
        />
      )}
    </>
  );
};

export default MdEqpSpecRegistPage;
